import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { toast } from "sonner";

import { DatePickerButton } from "@/components/DatePickerButton";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface CutoffCustomerRow {
  id: string;
  label: string;
  provider_id: string;
  review_cutoff_date: string | null;
  write_back_cutoff_date: string;
}

type CutoffColumn = "review_cutoff_date" | "write_back_cutoff_date";

// The generated Supabase `Database` type does not yet include customers.review_cutoff_date.
// Drop this facade once `pnpm --filter qa-app run sb:fetch-db-types` has been re-run against a
// project where migration 0298 (review_cutoff_date) is applied.
type UntypedResult = { data: unknown; error: { message: string } | null };
interface UntypedSelect {
  order: (col: string) => Promise<UntypedResult>;
}
interface UntypedFilter {
  eq: (col: string, val: string) => Promise<UntypedResult>;
}
interface UntypedQuery {
  select: (cols: string) => UntypedSelect;
  update: (patch: Record<string, unknown>) => UntypedFilter;
}
interface UntypedSupabase {
  from: (table: string) => UntypedQuery;
}
const untyped = supabase as unknown as UntypedSupabase;

export function calendarDateToUtcMidnightIso(date: Date): string {
  return `${dayjs(date).format("YYYY-MM-DD")}T00:00:00.000Z`;
}

export function isoToCalendarDate(iso: string | null): Date | null {
  if (!iso) return null;
  return dayjs(dayjs.utc(iso).format("YYYY-MM-DD")).toDate();
}

export default function CustomerReviewCutoffSection() {
  const queryClient = useQueryClient();

  const {
    data: customers,
    isError,
    error,
  } = useQuery({
    queryKey: ["customers", "for-review-cutoff"],
    queryFn: async () => {
      const { data, error } = await untyped
        .from("customers")
        .select("id,label,provider_id,review_cutoff_date,write_back_cutoff_date")
        .order("label");
      if (error) throw error;
      return (data ?? []) as CutoffCustomerRow[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, column, iso }: { id: string; column: CutoffColumn; iso: string | null }) => {
      const { error } = await untyped
        .from("customers")
        .update({ [column]: iso })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers", "for-review-cutoff"] });
      toast.success("Cut-off date saved");
    },
    onError: (error: Error) => toast.error(`Failed to save: ${error.message}`),
  });

  return (
    <div className="space-y-4 border rounded-lg p-6 bg-card">
      <h2 className="text-xl font-semibold">Cut-off Dates</h2>
      <p className="text-sm text-muted-foreground">
        Encounters discharged before a customer's review cut-off are excluded from the review queue; leave it empty to
        fall back to the global default. Nothing is written back for an encounter discharged before the write-back
        cut-off, which every customer has. Dates are stored as midnight UTC.
      </p>

      {isError ? (
        <p className="text-sm text-destructive">Failed to load customers: {error?.message ?? "unknown error"}</p>
      ) : customers === undefined ? (
        <p className="text-sm text-muted-foreground">Loading customers…</p>
      ) : customers.length === 0 ? (
        <p className="text-sm text-muted-foreground">No customers found.</p>
      ) : (
        <ul className="space-y-2">
          {customers.map((customer) => {
            const cutoffDate = isoToCalendarDate(customer.review_cutoff_date);
            const writeBackCutoffDate = isoToCalendarDate(customer.write_back_cutoff_date);
            const isSaving = saveMutation.isPending && saveMutation.variables?.id === customer.id;
            return (
              <li
                key={customer.id}
                className="flex items-center justify-between border rounded px-3 py-2 bg-background"
              >
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className={cn(
                      "h-2 w-2 rounded-full",
                      customer.review_cutoff_date ? "bg-primary" : "bg-muted-foreground/30",
                    )}
                  />
                  <span className="font-medium">{customer.label}</span>
                  <span className="text-sm text-muted-foreground">{customer.provider_id}</span>
                </div>
                <div className="flex items-center gap-2" aria-live="polite">
                  {isSaving && <span className="text-sm text-muted-foreground">Saving…</span>}
                  <span className="text-sm text-muted-foreground">Review</span>
                  <DatePickerButton
                    date={cutoffDate}
                    defaultMonth={cutoffDate ?? undefined}
                    disabled={isSaving}
                    placeholder="No cut-off"
                    ariaLabel={`Select cut-off date for ${customer.label}`}
                    clearAriaLabel={`Clear cut-off date for ${customer.label}`}
                    onDateChange={(d) =>
                      saveMutation.mutate({
                        id: customer.id,
                        column: "review_cutoff_date",
                        iso: d ? calendarDateToUtcMidnightIso(d) : null,
                      })
                    }
                  />
                  <span className="text-sm text-muted-foreground">Write-back</span>
                  <DatePickerButton
                    date={writeBackCutoffDate}
                    defaultMonth={writeBackCutoffDate ?? undefined}
                    disabled={isSaving}
                    placeholder="No cut-off"
                    ariaLabel={`Select write-back cut-off date for ${customer.label}`}
                    onDateChange={(d) => {
                      if (d === null) return;
                      saveMutation.mutate({
                        id: customer.id,
                        column: "write_back_cutoff_date",
                        iso: calendarDateToUtcMidnightIso(d),
                      });
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
